import { z } from "zod";
import { PositionEventZ } from "../webhooks/position";
import { PairStateZ } from "../domain/pair";

/**
 * Events the Worker pushes to the dashboard over the HedgePair DO's
 * WebSocket. Discriminated by `type`. The dashboard state store narrows
 * on `type` to update the right slice.
 */
export const PositionUpdateEventZ = z.object({
  type: z.literal("position:update"),
  payload: PositionEventZ,
});

export const PairStateEventZ = z.object({
  type: z.literal("pair:state"),
  payload: PairStateZ,
});

export const AccountStatusEventZ = z.object({
  type: z.literal("account:status"),
  payload: z.object({
    account: z.string(),
    online: z.boolean(),
    adapter: z.enum(["ea", "playwright"]),
    lastSeen: z.number().int(),
  }),
});

/**
 * Pair-level sleep/wake state change. Fired when the operator toggles
 * mode/manual_state on a pair, when the auto-mode idle deadline elapses,
 * or when one of the pair's leg workers reports an actual transition.
 * `legStates` carries each leg's last reported worker state so the
 * dashboard can show "partial" when the two legs disagree (rare — a
 * mid-transition snapshot). Dashboard subscribes via the HedgePair
 * socket; the BotChannel broadcasts to every HedgePair DO whose parent
 * `account_pairs.id` matches the affected pair.
 */
export const PairSleepStateEventZ = z.object({
  type: z.literal("pair:sleep-state"),
  payload: z.object({
    accountPairId: z.string(),
    mode: z.enum(["auto", "manual"]),
    manualState: z.enum(["awake", "asleep"]),
    /** Aggregated current state across both legs:
     *   awake     — both legs report awake
     *   asleep    — both legs report asleep
     *   partial   — legs disagree (mid-transition)
     *   unknown   — at least one leg has never acked
     *   orphan    — at least one leg's Brave didn't actually close on
     *               the last sleep attempt (operator must Retry Sleep
     *               or Force-kill from the dashboard) */
    currentState: z.enum(["awake", "asleep", "partial", "unknown", "orphan"]),
    autoSleepProtected: z.boolean(),
    /** Most-recent transition reason for log/feed display. */
    reason: z.string().max(64).nullable(),
    /** Per-leg ground truth keyed by accountId. */
    legStates: z.record(z.enum(["awake", "asleep", "unknown", "orphan"])),
    ts: z.number().int(),
  }),
});

/**
 * Lightweight equity tick — pushed by BotChannel through HedgePair to the
 * dashboard. Fires on every DOM-observable change (sub-second on busy
 * markets). Dashboard binds this directly to the equity ticker animation
 * without re-rendering the full position list.
 */
export const AccountTickEventZ = z.object({
  type: z.literal("account:tick"),
  payload: z.object({
    account: z.string(),
    equity: z.number(),
    balance: z.number(),
    marginLevel: z.number().nullable(),
    openPnl: z.number(),
    ts: z.number().int(),
  }),
});

export const HedgeDecisionEventZ = z.object({
  type: z.literal("hedge:decision"),
  payload: z.object({
    pair: z.string(),
    tier: z.enum(["haiku", "sonnet"]),
    action: z.string(),
    complianceStatus: z.string().nullable(),
    rulesChecked: z.array(z.string()).nullable(),
    reason: z.string(),
    confidence: z.number().min(0).max(1).nullable(),
    createdAt: z.number().int(),
  }),
});

export const HedgeExecutedEventZ = z.object({
  type: z.literal("hedge:executed"),
  payload: z.object({
    pair: z.string(),
    action: z.string(),
    account: z.string(),
    result: z.unknown(),
    withdrawn: z.number().nullable(),
  }),
});

export const AlertDrawdownEventZ = z.object({
  type: z.literal("alert:drawdown"),
  payload: z.object({
    account: z.string(),
    kind: z.enum(["daily", "max"]),
    used: z.number(),
    limit: z.number(),
    pct: z.number(),
  }),
});

export const AlertBreachEventZ = z.object({
  type: z.literal("alert:breach"),
  payload: z.object({
    account: z.string(),
    pair: z.string(),
    kind: z.string(),
    at: z.number().int(),
  }),
});

export const AlertNewsEventZ = z.object({
  type: z.literal("alert:news"),
  payload: z.object({
    event: z.string(),
    currency: z.string(),
    scheduledAt: z.number().int(),
    affectedPairs: z.array(z.string()),
    blackoutUntil: z.number().int(),
  }),
});

export const AlertComplianceEventZ = z.object({
  type: z.literal("alert:compliance"),
  payload: z.object({
    pair: z.string(),
    violation: z.string(),
    detail: z.string(),
    blockedAction: z.string(),
  }),
});

export const AlertDiscountEventZ = z.object({
  type: z.literal("alert:discount"),
  payload: z.object({
    slug: z.string(),
    accountSize: z.number(),
    oldPrice: z.number().nullable(),
    newPrice: z.number(),
    discountCode: z.string().nullable(),
    promoName: z.string().nullable(),
  }),
});

export const AlertAvailabilityEventZ = z.object({
  type: z.literal("alert:availability"),
  payload: z.object({
    slug: z.string(),
    turkeyAllowed: z.boolean(),
    prev: z.boolean(),
  }),
});

export const AlertRulesUpdatedEventZ = z.object({
  type: z.literal("alert:rules-updated"),
  payload: z.object({
    slug: z.string(),
    source: z.enum(["propfirmmatch", "official"]),
    rulesVersion: z.string(),
    changedSections: z.array(z.string()),
  }),
});

export const AlertRulesConflictEventZ = z.object({
  type: z.literal("alert:rules-conflict"),
  payload: z.object({
    slug: z.string(),
    fields: z.array(
      z.object({
        path: z.string(),
        pfm: z.unknown(),
        official: z.unknown(),
      }),
    ),
  }),
});

/**
 * Manual-trade Terminal events. Fired when the operator hits Execute
 * (one ack per account) and when a pending-order row transitions
 * (created / canceled / fired). Dashboard listens via the existing
 * pair-scoped WebSocket so the Terminal page doesn't need its own DO.
 */
export const ManualExecuteAckEventZ = z.object({
  type: z.literal("manual:execute-ack"),
  payload: z.object({
    accountId: z.string(),
    ok: z.boolean(),
    error: z.string().nullable(),
    at: z.number().int(),
  }),
});

export const PendingOrderChangedEventZ = z.object({
  type: z.literal("pending:changed"),
  payload: z.object({
    /** What happened to the row — the dashboard just re-fetches the
     *  pending list via SWR on any of these. Saving one event type
     *  over three keeps the discriminated union small. */
    change: z.enum(["created", "canceled", "fired", "expired"]),
    id: z.string(),
    accountId: z.string(),
    symbol: z.string(),
    /** Populated only on `fired`; null otherwise. */
    ticket: z.number().int().nullable(),
    at: z.number().int(),
  }),
});

export const ServerEventZ = z.discriminatedUnion("type", [
  PositionUpdateEventZ,
  PairStateEventZ,
  AccountStatusEventZ,
  PairSleepStateEventZ,
  AccountTickEventZ,
  HedgeDecisionEventZ,
  HedgeExecutedEventZ,
  AlertDrawdownEventZ,
  AlertBreachEventZ,
  AlertNewsEventZ,
  AlertComplianceEventZ,
  AlertDiscountEventZ,
  AlertAvailabilityEventZ,
  AlertRulesUpdatedEventZ,
  AlertRulesConflictEventZ,
  ManualExecuteAckEventZ,
  PendingOrderChangedEventZ,
]);
export type ServerEvent = z.infer<typeof ServerEventZ>;
export type AccountTickEvent = z.infer<typeof AccountTickEventZ>;
export type PairSleepStateEvent = z.infer<typeof PairSleepStateEventZ>;
export type ManualExecuteAckEvent = z.infer<typeof ManualExecuteAckEventZ>;
export type PendingOrderChangedEvent = z.infer<typeof PendingOrderChangedEventZ>;
